import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UNSAFE_DataRouterContext, type Location } from 'react-router';
import { UnsavedGuard } from './unsaved-guard';

/**
 * react-router는 blocker를 하나만 지원한다 — 둘 이상 등록되면 마지막 등록 함수만 검사한다.
 * 한 화면에 이탈 가드가 여럿 있을 수 있는 화면(지점 상세: 숨겨 둔 판매 탭 + 외부 연동 슬롯 패널 + 콘텐츠 편집)은
 * Provider 하나가 blocker 하나를 두고, 각 편집 영역은 DirtyGuard로 "저장하지 않은 입력이 있는가"와
 * "어떤 이동이 입력을 잃게 하는가"만 등록한다. 판정은 입력이 있는 영역들의 OR이다.
 */
interface Entry {
  when: boolean;
  includeSearch: boolean;
  leaves?: (current: Location, next: Location) => boolean;
}
interface Registry {
  set: (id: symbol, entry: Entry | null) => void;
}
const GuardRegistry = createContext<Registry | null>(null);

export function DirtyGuardProvider({ children }: { children: React.ReactNode }) {
  // 이동 판정은 단독 UnsavedGuard처럼 "마지막으로 그려진" 상태를 기준으로 한다 — 이동을 일으킨 같은 이벤트에서
  // 패널이 사라지며 입력이 해제돼도 그 이동은 여전히 막혀야 한다.
  const [entries, setEntries] = useState<ReadonlyMap<symbol, Entry>>(new Map());
  const set = useCallback((id: symbol, entry: Entry | null) => {
    setEntries((current) => {
      const next = new Map(current);
      if (entry) next.set(id, entry);
      else next.delete(id);
      return next;
    });
  }, []);
  const registry = useMemo(() => ({ set }), [set]);
  const active = useMemo(() => [...entries.values()].filter((item) => item.when), [entries]);
  const leaves = useCallback(
    (current: Location, next: Location) =>
      active.some((item) =>
        item.leaves
          ? item.leaves(current, next)
          : item.includeSearch && current.search !== next.search,
      ),
    [active],
  );
  return (
    <GuardRegistry.Provider value={registry}>
      <UnsavedGuard when={active.length > 0} leaves={leaves} />
      {children}
    </GuardRegistry.Provider>
  );
}

/** UnsavedGuard와 같은 입력 규칙. Provider 안이면 등록만 하고(blocker는 Provider 하나), 밖이면 자체 가드를 둔다. */
export function DirtyGuard({
  when,
  includeSearch = false,
  leaves,
}: {
  when: boolean;
  includeSearch?: boolean;
  leaves?: (current: Location, next: Location) => boolean;
}) {
  const registry = useContext(GuardRegistry);
  // 데이터 라우터 밖(구성요소 단독 렌더)에는 이동 자체가 없으므로 가드를 두지 않는다. useBlocker는 데이터 라우터가 필요하다.
  const inDataRouter = useContext(UNSAFE_DataRouterContext) !== null;
  const id = useRef(Symbol('dirty-guard'));
  useEffect(() => {
    registry?.set(id.current, { when, includeSearch, leaves });
  }, [registry, when, includeSearch, leaves]);
  useEffect(() => {
    const key = id.current;
    return () => {
      registry?.set(key, null);
    };
  }, [registry]);
  if (registry || !inDataRouter) return null;
  return <UnsavedGuard when={when} includeSearch={includeSearch} leaves={leaves} />;
}
